import { isServer } from 'solid-js/web';
import { z } from 'zod';

let ObjectIdClass: (new (...args: any[]) => any);
if (isServer) {
	ObjectIdClass = (await import ('mongodb')).ObjectId;
}

export function id()
{
	if (!isServer) 
		return z.object({
			_browserId: z.string()
		});

	return z.instanceof(ObjectIdClass);
}


/* Using strings so timezone can be added separately */
export const dateTime = z.string().refine(
	val => val.match(/^\d\d\d\d-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d$/)!=null,
	val => ({ message: `invalid datetime: ${val}` })
);	


export const date = z.string().refine(val => val.match(/^\d\d\d\d-[01]\d-[0-3]\d$/)!=null,{message:'invalid date'}); 
export const time = z.string().refine(val => val.match(/^[0-2]\d:[0-5]\d:[0-5]\d$/)!=null,{message:'invalid time'}); 

//TODO. Maybe a library - eg validator.js (bit large?), or FieldTypes might help
//XXX warning: in some places Im using a relative URL
export const url = z.string(); 
export type Url = z.infer<typeof url>;    

export const email = z.string();

export const facebook = z.string().max(128).regex(/^[^\/\s&]+$/);
export const instagram = z.string().max(128).regex(/^[^\/\s\?#&]+$/);
export const twitter = z.string().max(128).regex(/^[^\/\s\?#&]+$/);
export const soundcloud = z.string().max(128).regex(/^[^\/\s\?#&]+$/);


export const imageDetails = z.object({
	format: z.enum(['jpeg','png','webp']),
	width: z.number().min(0),
	height: z.number().min(0)
}).strict();

export type ImageDetails = z.infer<typeof imageDetails>;

export const imageFormats = z.object({
	full: imageDetails,
	large: imageDetails.optional(),
	medium: imageDetails.optional(),
	small: imageDetails.optional(),
	tiny: imageDetails.optional()
}).strict();
export type ImageFormats = z.infer<typeof imageFormats>;

export type ImageSize = keyof ImageFormats;

//XXX this is based on what is in the DB
export const image = z.object({
	creationTime: dateTime,
	modificationTime: dateTime,
	source: z.discriminatedUnion('type',[
		z.object({
			type: z.literal('upload'),
			personId: id()
		})
		//TODO add in others
	]),
	formats: imageFormats,
	hash: z.string(),  //XXX could check better

	/* Used browser-side */
	dev: z.boolean().optional(),
	uploading: z.string().optional(),
	url: url.optional(),
	/* Rendered posters only: whether the render still matches the record it was made from */
	isCurrent: z.boolean().optional()
}).strict();

export type ImageSchema = z.infer<typeof image>;


export const eventCalendarSettingsParent = z.object({
	allDay: z.boolean().optional(),
});


//TODO make all these calendar events strict... but currently there's cross-contamination between event types... 

export const eventCalendarOnceSettings = eventCalendarSettingsParent.extend({
	frequency: z.literal('once'),
	startSingle: dateTime,
	endSingle: dateTime.optional(),
});
export type EventCalendarOnceSettings = z.infer<typeof eventCalendarOnceSettings>;


export const eventCalendarRecurringSettings = eventCalendarSettingsParent.extend({
	frequency: z.enum(['daily','weekly','monthly']),
	startDate: date,
	endDate: date.optional(),
	startTime: time,
	endTime: time.optional(),
});
export type EventCalendarRecurringSettings = z.infer<typeof eventCalendarRecurringSettings>;


export const eventCalendarDailySettings = eventCalendarRecurringSettings.extend({
	frequency: z.literal('daily'),
});
export type EventCalendarDailySettings = z.infer<typeof eventCalendarDailySettings>;


export const day = z.enum(['mon','tue','wed','thu','fri','sat','sun']);
export type Day = z.infer<typeof day>;

export const eventCalendarWeeklySettings = eventCalendarRecurringSettings.extend({
	frequency: z.literal('weekly'),
	weekdays: z.array(day),
});
export type EventCalendarWeeklySettings = z.infer<typeof eventCalendarWeeklySettings>;

export const weekOfMonth = z.enum(['first','second','third','fourth','last']);
export type WeekOfMonth = z.infer<typeof weekOfMonth>;

export const eventCalendarMonthlySettings = eventCalendarRecurringSettings.extend({
	frequency: z.literal('monthly'),
	week: weekOfMonth,
	weekday: day
});
export type EventCalendarMonthlySettings = z.infer<typeof eventCalendarMonthlySettings>;


export const eventCalendarSettings = z.discriminatedUnion('frequency',[
	eventCalendarOnceSettings,
	eventCalendarDailySettings,
	eventCalendarWeeklySettings,
	eventCalendarMonthlySettings
]);
export type EventCalendarSettings = z.infer<typeof eventCalendarSettings>;

